import { ColDef, ICellRendererParams } from 'ag-grid-community'
import { Client, Group } from '@/types'
import { LicenseStatus } from '@/types/license.types'
import { Pencil, Trash, Prohibit, Play, XCircle } from 'phosphor-react'
import { getPlaceLabel } from '@/config/place.enum'
import {
  formatLicenseDate,
  getLicenseStatusClasses,
  getLicenseStatusLabel,
  resolveLicenseStatus,
} from '@/utils/license-utils'
import { isDirectClient } from '@/utils/client-scope'

interface ClientsColumnDefsParams {
  getGroupName: (group: string | Group) => string
  handleEdit: (client: Client) => void
  handleDeleteClick: (id: string) => void
  handleBlock: (client: Client) => void
  handleUnblock: (client: Client) => void
  handleTerminateClick: (client: Client) => void
  canEdit: boolean
  showResellerColumn?: boolean
}

export const getClientsColumnDefs = ({
  getGroupName,
  handleEdit,
  handleDeleteClick,
  handleBlock,
  handleUnblock,
  handleTerminateClick,
  canEdit,
  showResellerColumn = false,
}: ClientsColumnDefsParams): ColDef[] => {
  const columns: ColDef[] = [
    {
      field: 'name',
      headerName: 'Raison sociale',
      flex: 1,
      minWidth: 150,
    },
    {
      field: 'firstname',
      headerName: 'Prénom',
      flex: 1,
      minWidth: 120,
    },
    {
      field: 'lastname',
      headerName: 'Nom',
      flex: 1,
      minWidth: 120,
    },
    {
      field: 'email',
      headerName: 'Email',
      flex: 1,
      minWidth: 180,
    },
    {
      field: 'phone',
      headerName: 'Téléphone',
      flex: 1,
      minWidth: 120,
    },
    {
      field: 'group',
      headerName: 'Groupe',
      flex: 1,
      minWidth: 150,
      valueGetter: params => getGroupName(params.data?.group || ''),
    },
    ...(showResellerColumn
      ? [
          {
            field: 'reseller',
            headerName: 'Revendeur',
            flex: 1,
            minWidth: 150,
            valueGetter: params => {
              const reseller = params.data?.reseller
              if (!reseller || typeof reseller === 'string') return '-'
              const name = `${reseller.firstname ?? ''} ${reseller.lastname ?? ''}`.trim()
              return name || reseller.email || '-'
            },
            filter: 'agTextColumnFilter',
          } as ColDef,
        ]
      : []),
    {
      field: 'fluxId',
      headerName: 'Identifiant Flux',
      width: 150,
      minWidth: 150,
      valueGetter: params => {
        const client = params.data as Client | undefined
        if (!client || !isDirectClient(client)) return null
        return client.fluxId ?? null
      },
      valueFormatter: params => (params.value != null ? String(params.value) : '—'),
      filter: 'agNumberColumnFilter',
    },
    {
      field: 'siret',
      headerName: 'SIRET',
      width: 160,
      minWidth: 160,
      valueGetter: params => {
        const client = params.data as Client | undefined
        if (!client || !isDirectClient(client)) return null
        return client.siret ?? null
      },
      valueFormatter: params => (params.value ? String(params.value) : '—'),
      filter: 'agTextColumnFilter',
    },
    {
      field: 'noSerie',
      headerName: 'N° Série',
      flex: 1,
      minWidth: 150,
    },
    {
      field: 'noPS',
      headerName: 'N° PS',
      flex: 1,
      minWidth: 100,
    },
    {
      field: 'subscribedAt',
      headerName: 'Date de souscription',
      flex: 1,
      minWidth: 170,
      filter: 'agDateColumnFilter',
      valueGetter: params => params.data?.subscribedAt ?? params.data?.createdAt,
      valueFormatter: params => formatLicenseDate(params.value),
    },
    {
      field: 'licenseStatus',
      headerName: 'Statut licence',
      width: 130,
      valueGetter: params =>
        params.data ? getLicenseStatusLabel(params.data as Client) : '',
      cellRenderer: (params: ICellRendererParams<Client>) => {
        if (!params.data) return null
        const status = resolveLicenseStatus(params.data)
        return (
          <span
            className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium ${getLicenseStatusClasses(status)}`}
          >
            {getLicenseStatusLabel(params.data)}
          </span>
        )
      },
      filter: 'agTextColumnFilter',
    },
    {
      field: 'terminatedAt',
      headerName: 'Date de résiliation',
      flex: 1,
      minWidth: 160,
      filter: 'agDateColumnFilter',
      valueFormatter: params => formatLicenseDate(params.value),
    },
    {
      field: 'noticeEndAt',
      headerName: 'Fin de préavis',
      flex: 1,
      minWidth: 150,
      filter: 'agDateColumnFilter',
      valueFormatter: params => formatLicenseDate(params.value),
    },
    {
      field: 'address',
      headerName: 'Adresse',
      flex: 1,
      minWidth: 150,
    },
    {
      field: 'city',
      headerName: 'Ville',
      flex: 1,
      minWidth: 120,
    },
    {
      field: 'postalCode',
      headerName: 'Code postal',
      width: 120,
    },
    {
      field: 'place',
      headerName: 'Lieu',
      flex: 1,
      minWidth: 200,
      valueGetter: params => {
        if (!params.data?.place) return ''
        return getPlaceLabel(params.data.place)
      },
      filter: 'agTextColumnFilter',
    },
    {
      field: 'lastService',
      headerName: 'Dernier service',
      flex: 1,
      minWidth: 150,
      filter: 'agDateColumnFilter',
      valueFormatter: params => formatLicenseDate(params.value),
    },
    {
      field: 'autGIE',
      headerName: 'Aut. GIE',
      width: 100,
      cellRenderer: (params: ICellRendererParams) => (
        <span
          className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium ${
            params.value
              ? 'bg-[var(--color-success)]/10 text-[var(--color-success)]'
              : 'bg-[var(--color-gray-200)]/10 text-[var(--color-text-muted)]'
          }`}
        >
          {params.value ? 'Oui' : 'Non'}
        </span>
      ),
      filter: 'agTextColumnFilter',
      filterValueGetter: params => {
        if (params.data?.autGIE === undefined || params.data?.autGIE === null) return ''
        return params.data.autGIE ? 'Oui' : 'Non'
      },
      filterParams: {
        filterOptions: ['equals'],
      },
    },
    {
      field: 'autPublic',
      headerName: 'Aut. Public',
      width: 120,
      cellRenderer: (params: ICellRendererParams) => (
        <span
          className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium ${
            params.value
              ? 'bg-[var(--color-success)]/10 text-[var(--color-success)]'
              : 'bg-[var(--color-gray-200)]/10 text-[var(--color-text-muted)]'
          }`}
        >
          {params.value ? 'Oui' : 'Non'}
        </span>
      ),
      filter: 'agTextColumnFilter',
      filterValueGetter: params => {
        if (params.data?.autPublic === undefined || params.data?.autPublic === null) return ''
        return params.data.autPublic ? 'Oui' : 'Non'
      },
      filterParams: {
        filterOptions: ['equals'],
      },
    },
    {
      field: 'updatedAt',
      headerName: 'Dernière modification',
      flex: 1,
      minWidth: 180,
      filter: 'agDateColumnFilter',
      valueFormatter: params => formatLicenseDate(params.value),
    },
  ]

  const actionsColumn: ColDef = {
    headerName: 'Actions',
    width: 200,
    cellRenderer: (params: ICellRendererParams) => {
      const client = params.data as Client
      if (!client) return null

      const status = resolveLicenseStatus(client)
      const isTerminated = status === LicenseStatus.TERMINATED
      const isBlocked = status === LicenseStatus.BLOCKED

      return (
        <div className="flex items-center gap-1 h-full">
          <button
            onClick={() => handleEdit(client)}
            disabled={isTerminated}
            className="flex items-center justify-center w-8 h-8 rounded-lg text-[var(--color-primary)] hover:bg-[var(--color-primary)]/10 transition-all duration-200 disabled:opacity-40 disabled:cursor-not-allowed"
            title="Modifier"
          >
            <Pencil className="w-4 h-4" weight="regular" />
          </button>
          {!isTerminated && !isBlocked && (
            <button
              onClick={() => handleBlock(client)}
              className="flex items-center justify-center w-8 h-8 rounded-lg text-[var(--color-warning)] hover:bg-[var(--color-warning)]/10 transition-all duration-200"
              title="Bloquer"
            >
              <Prohibit className="w-4 h-4" weight="regular" />
            </button>
          )}
          {!isTerminated && isBlocked && (
            <button
              onClick={() => handleUnblock(client)}
              className="flex items-center justify-center w-8 h-8 rounded-lg text-[var(--color-info)] hover:bg-[var(--color-info)]/10 transition-all duration-200"
              title="Débloquer"
            >
              <Play className="w-4 h-4" weight="regular" />
            </button>
          )}
          {!isTerminated && (
            <button
              onClick={() => handleTerminateClick(client)}
              className="flex items-center justify-center w-8 h-8 rounded-lg text-[var(--color-error)] hover:bg-[var(--color-error)]/10 transition-all duration-200"
              title="Résilier"
            >
              <XCircle className="w-4 h-4" weight="regular" />
            </button>
          )}
          {!isTerminated && (
            <button
              onClick={() => handleDeleteClick(client._id)}
              className="flex items-center justify-center w-8 h-8 rounded-lg text-[var(--color-error)] hover:bg-[var(--color-error)]/10 transition-all duration-200"
              title="Archiver"
            >
              <Trash className="w-4 h-4" weight="regular" />
            </button>
          )}
        </div>
      )
    },
    pinned: 'right',
  }

  if (canEdit) {
    columns.push(actionsColumn)
  }

  return columns
}
